<template>
    <section class="tw:flex tw:flex-col tw:overflow-hidden tw:rounded-lg tw:bg-white tw:shadow-md">
        <div
            v-if="title || subtitle || showSearch || $slots.toolbar"
            class="tw:border-b tw:border-slate-100 tw:bg-white"
        >
            <div class="tw:flex tw:flex-col tw:gap-4 tw:px-4 tw:py-4 tw:sm:px-6 tw:sm:py-5">
                <div
                    v-if="title || subtitle || $slots.toolbar"
                    class="tw:flex tw:flex-col tw:gap-4 tw:md:flex-row tw:md:items-start tw:md:justify-between"
                >
                    <div v-if="title || subtitle" class="tw:min-w-0 tw:flex-1">
                        <h2 v-if="title" class="tw:mb-0 tw:text-lg tw:font-black tw:text-slate-900">
                            {{ title }}
                        </h2>
                        <p v-if="subtitle" class="tw:mt-1 tw:mb-0 tw:text-sm tw:text-slate-500">
                            {{ subtitle }}
                        </p>
                    </div>

                    <div v-if="$slots.toolbar" class="tw:flex tw:flex-wrap tw:items-center tw:gap-3">
                        <slot name="toolbar"></slot>
                    </div>
                </div>

                <label v-if="showSearch" class="tw:relative tw:block tw:w-full tw:max-w-md">
                    <span class="tw:pointer-events-none tw:absolute tw:left-4 tw:top-1/2 tw:-translate-y-1/2 tw:text-slate-400">
                        <i class="material-icons tw:text-base" aria-hidden="true">search</i>
                    </span>
                    <input
                        :value="searchValue"
                        type="search"
                        :placeholder="resolvedSearchPlaceholder"
                        class="tw:block tw:w-full tw:rounded-md tw:border tw:border-slate-200 tw:bg-slate-50 tw:py-2.5 tw:pl-11 tw:pr-4 tw:text-sm tw:text-slate-700 tw:transition-colors tw:duration-200 tw:placeholder:text-slate-400 tw:focus:border-slate-300 tw:focus:bg-white tw:focus:outline-none"
                        @input="onSearchInput"
                    />
                </label>
            </div>
        </div>

        <div
            v-if="visibleColumns.length > 0"
            class="tw:hidden tw:border-b tw:border-slate-100 tw:bg-slate-50 tw:md:block"
        >
            <div
                class="tw:grid tw:gap-4 tw:px-4 tw:py-3 tw:text-[11px] tw:font-bold tw:uppercase tw:tracking-[0.18em] tw:text-slate-400 tw:sm:px-6"
                :style="desktopGridStyle"
            >
                <div
                    v-for="column in visibleColumns"
                    :key="column.key"
                    class="tw:flex tw:min-h-[1rem] tw:min-w-0 tw:items-center"
                    :class="[resolveHeaderAlignClass(column), column.headerClass]"
                >
                    <slot :name="`header-${column.key}`" :column="column">
                        {{ column.label }}
                    </slot>
                </div>
            </div>
        </div>

        <div
            class="tw:min-h-0"
            :class="!hasPagination && maxBodyHeight ? 'tw:overflow-y-auto' : ''"
            :style="bodyStyle"
        >
            <div v-if="items.length > 0" class="tw:flex tw:flex-col">
                <article
                    v-for="(item, index) in items"
                    :key="resolveRowKey(item, index)"
                    class="tw:flex tw:flex-col tw:gap-3 tw:border-b tw:border-slate-100 tw:px-4 tw:py-4 tw:last:border-b-0 tw:sm:px-6 tw:md:grid tw:md:items-center tw:md:gap-4"
                    :style="desktopGridStyle"
                >
                    <div
                        v-for="column in visibleColumns"
                        :key="column.key"
                        class="tw:flex tw:min-w-0 tw:flex-col tw:gap-1"
                        :class="column.cellClass"
                    >
                        <p class="tw:mb-0 tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-[0.08em] tw:text-slate-400 tw:md:hidden">
                            {{ column.mobileLabel || column.label }}
                        </p>

                        <div class="tw:min-w-0" :class="resolveCellAlignClass(column)">
                            <slot
                                :name="`cell-${column.key}`"
                                :item="item"
                                :column="column"
                                :value="resolveCellValue(item, column, index)"
                                :index="index"
                            >
                                <p
                                    class="tw:mb-0 tw:text-sm tw:font-medium tw:text-slate-600"
                                    :class="resolveValueClass(column, item, index)"
                                >
                                    {{ resolveCellValue(item, column, index) }}
                                </p>
                            </slot>
                        </div>
                    </div>
                </article>
            </div>

            <div v-else class="tw:px-4 tw:py-16 tw:text-center tw:sm:px-6">
                <slot name="empty" :search="searchValue">
                    <i class="material-icons tw:text-5xl tw:text-slate-300">{{ emptyIcon }}</i>
                    <p class="tw:mt-4 tw:font-semibold tw:text-slate-700">
                        {{ resolvedEmptyTitle }}
                    </p>
                    <p v-if="emptyDescription" class="tw:mt-1 tw:text-sm tw:text-slate-400">
                        {{ emptyDescription }}
                    </p>
                </slot>
            </div>
        </div>

        <div
            v-if="$slots.footer || $slots.pagination"
            class="tw:border-t tw:border-slate-100 tw:bg-slate-50"
        >
            <div class="tw:flex tw:flex-col tw:gap-4 tw:px-4 tw:py-4 tw:sm:px-6 tw:sm:py-5">
                <slot name="footer" :items="items" :search="searchValue"></slot>
                <slot name="pagination"></slot>
            </div>
        </div>
    </section>
</template>

<script setup>
    import { computed } from 'vue';
    import { getI18n, t } from '@/utils/i18n-helper';

    const i18n = getI18n();

    const props = defineProps({
        title: {
            type: String,
            default: '',
        },
        subtitle: {
            type: String,
            default: '',
        },
        columns: {
            type: Array,
            required: true,
        },
        items: {
            type: Array,
            default: () => [],
        },
        rowKey: {
            type: [String, Function],
            default: 'id',
        },
        gridTemplateColumns: {
            type: String,
            default: '',
        },
        showSearch: {
            type: Boolean,
            default: false,
        },
        searchValue: {
            type: String,
            default: '',
        },
        searchPlaceholder: {
            type: String,
            default: '',
        },
        pagination: {
            type: Object,
            default: null,
        },
        emptyIcon: {
            type: String,
            default: 'inbox',
        },
        emptyTitle: {
            type: String,
            default: '',
        },
        emptyDescription: {
            type: String,
            default: '',
        },
        maxBodyHeight: {
            type: String,
            default: '',
        },
    });

    const emit = defineEmits(['update:search', 'update:page', 'change:per-page']);

    const currentLocale = computed(() => i18n?.locale || 'es');

    const visibleColumns = computed(() => props.columns.filter(column => !column.hidden));

    const resolvedSearchPlaceholder = computed(() => {
        const locale = currentLocale.value;
        return props.searchPlaceholder || (locale && t('general.buscar', {}, 'Buscar'));
    });

    const resolvedEmptyTitle = computed(() => {
        const locale = currentLocale.value;
        return props.emptyTitle || (locale && t('general.sin_datos_disponibles', {}, 'No hay datos disponibles'));
    });

    const desktopGridStyle = computed(() => ({
        gridTemplateColumns: props.gridTemplateColumns || `repeat(${Math.max(visibleColumns.value.length, 1)}, minmax(0, 1fr))`,
    }));

    const hasPagination = computed(() => Boolean(props.pagination));

    const bodyStyle = computed(() => {
        if (hasPagination.value || !props.maxBodyHeight) {
            return null;
        }

        return { maxHeight: props.maxBodyHeight };
    });

    function onSearchInput(event) {
        emit('update:search', event.target.value);
    }

    function resolveRowKey(item, index) {
        if (typeof props.rowKey === 'function') {
            return props.rowKey(item, index);
        }

        return item?.[props.rowKey] ?? `${props.rowKey}-${index}`;
    }

    function resolveRawCellValue(item, column) {
        const field = column.field || column.key;
        return item?.[field];
    }

    function resolveCellValue(item, column, index) {
        const rawValue = resolveRawCellValue(item, column);

        if (typeof column.formatter === 'function') {
            return column.formatter(rawValue, item, index);
        }

        return rawValue;
    }

    function resolveHeaderAlignClass(column) {
        if (column.align === 'right') {
            return 'tw:justify-end tw:text-right';
        }

        if (column.align === 'center') {
            return 'tw:justify-center tw:text-center';
        }

        return 'tw:justify-start tw:text-left';
    }

    function resolveCellAlignClass(column) {
        if (column.align === 'right') {
            return 'tw:md:text-right';
        }

        if (column.align === 'center') {
            return 'tw:md:text-center';
        }

        return 'tw:md:text-left';
    }

    function resolveValueClass(column, item, index) {
        if (typeof column.valueClass === 'function') {
            return column.valueClass(resolveCellValue(item, column, index), item, index, column);
        }

        return column.valueClass || '';
    }
</script>
